import React, { useEffect, useState } from 'react';
import {
    Table, Input, Space, Typography, Button, Tag, Modal, Form,
    InputNumber, Select, message, Tooltip, Popconfirm, Divider, Checkbox,
} from 'antd';
import { DollarOutlined, CopyOutlined, CreditCardOutlined, StopOutlined, CheckCircleOutlined, SafetyOutlined, DisconnectOutlined, CloudUploadOutlined } from '@ant-design/icons';
import api from '../utils/api';
import { useNavigate } from 'react-router-dom';

const { Title } = Typography;
const { Search } = Input;
const { Option } = Select;

// ── Product Pricing Modal ─────────────────────────────────────────────────────
const PRODUCTS = ['BAR', 'RBAC', 'EAA', 'AIS', 'DMS', 'AIBS', 'CRM'];

const PricingModal = ({ visible, onClose }) => {
    const [prices, setPrices] = useState([]);
    const [loading, setLoading] = useState(false);
    const [form] = Form.useForm();

    const getCookieToken = () => {
        const value = `; ${document.cookie}`;
        const parts = value.split('; token=');
        if (parts.length === 2) return parts.pop().split(';').shift();
        return null;
    };

    const fetchPrices = async () => {
        try {
            const token = getCookieToken();
            const res = await api.get('/product-prices', { headers: { Authorization: `Bearer ${token}` } });
            setPrices(res.data);
        } catch {
            message.error('Failed to load product prices.');
        }
    };

    useEffect(() => { if (visible) fetchPrices(); }, [visible]);

    const handleSave = async (values) => {
        setLoading(true);
        try {
            const token = getCookieToken();
            await api.post('/set-product-price', { productName: values.productName, priceINR: values.priceINR }, { headers: { Authorization: `Bearer ${token}` } });
            message.success(`Price for ${values.productName} saved as ₹${values.priceINR}/year`);
            form.resetFields();
            fetchPrices();
        } catch {
            message.error('Failed to save price.');
        } finally {
            setLoading(false);
        }
    };

    const priceColumns = [
        { title: 'Product', dataIndex: 'productName', key: 'productName' },
        { title: 'Yearly Price (₹)', dataIndex: 'priceINR', key: 'priceINR', render: (v) => `₹${v?.toLocaleString('en-IN')}` },
        { title: 'Last Updated', dataIndex: 'updatedAt', key: 'updatedAt', render: (d) => d ? new Date(d).toLocaleDateString() : '—' },
    ];

    return (
        <Modal title="Product Pricing (Yearly)" open={visible} onCancel={onClose} footer={null} width={600}>
            <Table dataSource={prices} columns={priceColumns} rowKey="productName" size="small" pagination={false} style={{ marginBottom: 24 }} />
            <Title level={5}>Set / Update Price</Title>
            <Form form={form} layout="inline" onFinish={handleSave}>
                <Form.Item name="productName" rules={[{ required: true }]}>
                    <Select placeholder="Product" style={{ width: 120 }}>
                        {PRODUCTS.map((p) => <Option key={p} value={p}>{p}</Option>)}
                    </Select>
                </Form.Item>
                <Form.Item name="priceINR" rules={[{ required: true, type: 'number', min: 0 }]}>
                    <InputNumber placeholder="Price (₹)" min={0} prefix="₹" />
                </Form.Item>
                <Form.Item>
                    <Button type="primary" htmlType="submit" loading={loading}>Save</Button>
                </Form.Item>
            </Form>
        </Modal>
    );
};

// ── License IP Whitelist Modal ────────────────────────────────────────────────
// Our test machines: heartbeats from these IPs run without taking the
// customer's single instance slot (RBAC, BAR, MAA/EAA, CRM).
const INSTANCE_PRODUCTS = ['RBAC', 'BAR', 'EAA', 'CRM'];

const WhitelistModal = ({ visible, onClose, getToken }) => {
    const [entries, setEntries] = useState([]);
    const [loading, setLoading] = useState(false);
    const [form] = Form.useForm();

    const fetchEntries = async () => {
        try {
            const res = await api.get('/admin/license-ip-whitelist', { headers: { Authorization: `Bearer ${getToken()}` } });
            setEntries(res.data);
        } catch {
            message.error('Failed to load IP whitelist.');
        }
    };

    useEffect(() => { if (visible) fetchEntries(); }, [visible]);

    const handleAdd = async (values) => {
        setLoading(true);
        try {
            await api.post('/admin/license-ip-whitelist', { ip: values.ip.trim(), note: values.note }, { headers: { Authorization: `Bearer ${getToken()}` } });
            message.success(`${values.ip.trim()} whitelisted`);
            form.resetFields();
            fetchEntries();
        } catch (err) {
            message.error(err.response?.data?.message || 'Failed to add IP.');
        } finally {
            setLoading(false);
        }
    };

    const handleRemove = async (ip) => {
        try {
            await api.post('/admin/license-ip-whitelist/remove', { ip }, { headers: { Authorization: `Bearer ${getToken()}` } });
            message.success(`${ip} removed`);
            fetchEntries();
        } catch (err) {
            message.error(err.response?.data?.message || 'Failed to remove IP.');
        }
    };

    const entryColumns = [
        { title: 'IP / CIDR', dataIndex: 'ip', key: 'ip', render: (ip) => <Typography.Text code>{ip}</Typography.Text> },
        { title: 'Note', dataIndex: 'note', key: 'note', render: (n) => n || '—' },
        { title: 'Added By', dataIndex: 'createdBy', key: 'createdBy', render: (v) => v || '—' },
        {
            title: '',
            key: 'remove',
            render: (_, record) => (
                <Popconfirm title={`Remove ${record.ip}?`} onConfirm={() => handleRemove(record.ip)} okText="Remove" okType="danger">
                    <Button size="small" danger>Remove</Button>
                </Popconfirm>
            ),
        },
    ];

    return (
        <Modal title="License IP Whitelist (test machines)" open={visible} onCancel={onClose} footer={null} width={700}>
            <Table dataSource={entries} columns={entryColumns} rowKey="ip" size="small" pagination={false} style={{ marginBottom: 24 }} />
            <Form form={form} layout="inline" onFinish={handleAdd}>
                <Form.Item name="ip" rules={[{ required: true, message: 'IP or CIDR' }]}>
                    <Input placeholder="203.0.113.7 or 203.0.113.0/24" style={{ width: 240 }} />
                </Form.Item>
                <Form.Item name="note">
                    <Input placeholder="Note (e.g. QA office)" style={{ width: 200 }} />
                </Form.Item>
                <Form.Item>
                    <Button type="primary" htmlType="submit" loading={loading}>Add</Button>
                </Form.Item>
            </Form>
        </Modal>
    );
};

// ── Dashboard ─────────────────────────────────────────────────────────────────
const Dashboard = () => {
    const [data, setData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [editCounts, setEditCounts] = useState({});
    const [pricingVisible, setPricingVisible] = useState(false);
    const [tierLoading, setTierLoading] = useState({});
    const [eulaLoading, setEulaLoading] = useState({});
    const [paymentLinkLoading, setPaymentLinkLoading] = useState({});
    const [suspendedRows, setSuspendedRows] = useState([]);
    const [unsuspendLoading, setUnsuspendLoading] = useState({});
    const [bulkEulaLoading, setBulkEulaLoading] = useState(false);
    const [revokeAllProducts, setRevokeAllProducts] = useState(false);
    const [revokeProducts, setRevokeProducts] = useState(['BAR']);
    const [backupJobs, setBackupJobs] = useState({});
    const [backupLoading, setBackupLoading] = useState({});
    const [whitelistVisible, setWhitelistVisible] = useState(false);
    const [releaseLoading, setReleaseLoading] = useState({});

    const navigate = useNavigate();
    const getToken = () => {
        const value = `; ${document.cookie}`;
        const parts = value.split('; token=');
        if (parts.length === 2) return parts.pop().split(';').shift();
        return null;
    };

    const handleCountChange = (userId, value) => {
        if (/^\d*$/.test(value)) {
            setEditCounts(prev => ({ ...prev, [userId]: value }));
        }
    };

    const saveViolationCount = async (userId) => {
        const count = editCounts[userId];
        if (count === undefined || count === '') return;

        try {
            await api.post('/modify-violation-count', { userId, violationValidationCount: Number(count) }, { headers: { Authorization: `Bearer ${getToken()}` } });
            const newData = data.map(user => user._id === userId ? { ...user, violationValidationCount: Number(count) } : user);
            setData(newData);
            setFilteredData(newData);
            setEditCounts(prev => ({ ...prev, [userId]: undefined }));
        } catch {
            message.error('Failed to update violation count.');
        }
    };

    const fetchBackupJobs = async (users = data) => {
        const ids = users.flatMap(u => u.subscriptions || []).filter(s => s.productName === 'BAR' && s.isCloud).map(s => s._id);
        if (!ids.length) return;
        try {
            const response = await api.get('/bar-backups/latest', { params: { subscriptionIds: ids.join(',') },
                headers: { Authorization: 'Bearer ' + getToken() } });
            setBackupJobs(Object.fromEntries(response.data.map(j => [j.subscriptionId, j])));
        } catch (err) {
            if (err.response?.status !== 403) console.error('Failed to refresh BAR backups', err);
        }
    };

    // Fetch dashboard data
    const fetchData = async () => {
        try {
            const response = await api.get('/admin-dashboard', { headers: { Authorization: `Bearer ${getToken()}` } });
            setData(response.data);
            setFilteredData(response.data);
            fetchBackupJobs(response.data);
            // Derive suspended EAA subscriptions across all users
            const suspended = [];
            response.data.forEach(user => {
                (user.subscriptions || []).forEach(sub => {
                    if (sub.eaaIsBlocked) {
                        suspended.push({
                            key: sub._id,
                            subscriptionId: sub._id,
                            licenseKey: sub.licenseKey,
                            username: user.username,
                            email: user.email,
                            emailIdentity: sub.emailIdentity || '—',
                            blockReason: sub.eaaBlockReason || 'Suspended',
                        });
                    }
                });
            });
            setSuspendedRows(suspended);
        } catch {
            message.error('Session expired. Redirecting to login.');
            navigate('/');
        }
    };

    const handleUnsuspend = async (licenseKey, subscriptionId) => {
        setUnsuspendLoading(prev => ({ ...prev, [subscriptionId]: true }));
        try {
            await api.post('/eaa/unsuspend', { licenseKey }, { headers: { Authorization: `Bearer ${getToken()}` } });
            message.success('Subscription unsuspended successfully.');
            setSuspendedRows(prev => prev.filter(r => r.subscriptionId !== subscriptionId));
        } catch (err) {
            message.error(err.response?.data?.message || 'Failed to unsuspend subscription.');
        } finally {
            setUnsuspendLoading(prev => ({ ...prev, [subscriptionId]: false }));
        }
    };

    useEffect(() => { fetchData(); }, []);
    useEffect(() => {
        const interval = setInterval(() => {
            if (Object.values(backupJobs).some(j => ['queued', 'creating', 'preparing', 'uploading'].includes(j.stage))) fetchBackupJobs();
        }, 5000);
        return () => clearInterval(interval);
    }, [backupJobs, data]);

    const handleBackup = async (subscriptionId) => {
        setBackupLoading(prev => ({ ...prev, [subscriptionId]: true }));
        try {
            const response = await api.post('/bar-backups', { subscriptionId },
                { headers: { Authorization: 'Bearer ' + getToken() } });
            setBackupJobs(prev => ({ ...prev, [subscriptionId]: response.data.job }));
            message.success(response.data.deduplicated ? 'An active backup already exists.' : 'BAR backup queued.');
        } catch (err) {
            message.error(err.response?.data?.message || 'Failed to queue BAR backup.');
        } finally {
            setBackupLoading(prev => ({ ...prev, [subscriptionId]: false }));
        }
    };

    const onSearch = (value) => {
        const filtered = data.filter((user) =>
            user.username.toLowerCase().includes(value.toLowerCase()) ||
            user.email.toLowerCase().includes(value.toLowerCase())
        );
        setFilteredData(filtered);
    };

    // Toggle the free/paid tier of a single product (subscription).
    const handleToggleSubTier = async (subscriptionId, currentTier) => {
        const newTier = currentTier === 'paid' ? 'free' : 'paid';
        setTierLoading(prev => ({ ...prev, [subscriptionId]: true }));
        try {
            await api.post('/set-subscription-tier', { subscriptionId, tier: newTier }, { headers: { Authorization: `Bearer ${getToken()}` } });
            message.success(`Product tier updated to ${newTier}`);
            const updateSubs = (u) => ({
                ...u,
                subscriptions: (u.subscriptions || []).map(s =>
                    s._id === subscriptionId ? { ...s, tier: newTier } : s
                ),
            });
            const newData = data.map(updateSubs);
            setData(newData);
            setFilteredData(prev => prev.map(updateSubs));
        } catch {
            message.error('Failed to update product tier.');
        } finally {
            setTierLoading(prev => ({ ...prev, [subscriptionId]: false }));
        }
    };

    const handleSetEulaStatus = async (userId, subscriptionId, productName, currentAccepted) => {
        const nextAccepted = !Boolean(currentAccepted);
        setEulaLoading(prev => ({ ...prev, [subscriptionId]: true }));
        try {
            const res = await api.post(
                '/set-subscription-eula',
                { subscriptionId, userId, productName, eulaAccepted: nextAccepted },
                { headers: { Authorization: 'Bearer ' + getToken() } }
            );
            const updateSubs = (u) => ({
                ...u,
                subscriptions: (u.subscriptions || []).map(s =>
                    s._id === subscriptionId
                        ? { ...s, eulaAccepted: res.data.eulaAccepted, eulaAcceptedAt: res.data.eulaAcceptedAt }
                        : s
                ),
            });
            const newData = data.map(updateSubs);
            setData(newData);
            setFilteredData(prev => prev.map(updateSubs));
            message.success(res.data.message || ('EULA marked as ' + (nextAccepted ? 'agreed' : 'unaccepted') + '.'));
        } catch (err) {
            message.error(err.response?.data?.message || 'Failed to update EULA status.');
        } finally {
            setEulaLoading(prev => ({ ...prev, [subscriptionId]: false }));
        }
    };

    const handleRevokeEulaAcceptance = async () => {
        const selectedProducts = revokeAllProducts ? PRODUCTS : revokeProducts;
        if (!selectedProducts.length) {
            message.warning('Select at least one product.');
            return;
        }

        setBulkEulaLoading(true);
        try {
            const res = await api.post(
                '/revoke-eula-acceptance',
                { allProducts: revokeAllProducts, products: selectedProducts },
                { headers: { Authorization: 'Bearer ' + getToken() } }
            );
            const revokedProducts = new Set((res.data.products || selectedProducts).map(p => String(p).toUpperCase()));
            const clearEula = (u) => ({
                ...u,
                subscriptions: (u.subscriptions || []).map(s =>
                    revokedProducts.has(String(s.productName || '').toUpperCase())
                        ? { ...s, eulaAccepted: false, eulaAcceptedAt: null }
                        : s
                ),
            });
            setData(prev => prev.map(clearEula));
            setFilteredData(prev => prev.map(clearEula));
            const revokedCount = Number(res.data.revokedCount || 0);
            message.success((res.data.message || 'EULA acceptance revoked.') + ' Updated subscriptions: ' + revokedCount + '.');
        } catch (err) {
            message.error(err.response?.data?.message || 'Failed to revoke EULA acceptance.');
        } finally {
            setBulkEulaLoading(false);
        }
    };
    // Frees the subscription's instance slot so the customer's new server can claim it.
    const handleReleaseInstance = async (subscriptionId) => {
        setReleaseLoading(prev => ({ ...prev, [subscriptionId]: true }));
        try {
            const res = await api.post('/admin/release-license-instance', { subscriptionId }, { headers: { Authorization: `Bearer ${getToken()}` } });
            const clearInstance = (u) => ({
                ...u,
                subscriptions: (u.subscriptions || []).map(s =>
                    s._id === subscriptionId ? { ...s, activeInstance: undefined } : s
                ),
            });
            setData(prev => prev.map(clearInstance));
            setFilteredData(prev => prev.map(clearInstance));
            message.success(res.data.message || 'License instance released.');
        } catch (err) {
            message.error(err.response?.data?.message || 'Failed to release license instance.');
        } finally {
            setReleaseLoading(prev => ({ ...prev, [subscriptionId]: false }));
        }
    };

    const handleCreatePaymentLink = async (subscriptionId, subTier) => {
        if (subTier !== 'paid') {
            message.warning('Product must be on Paid tier to create a payment link.');
            return;
        }
        setPaymentLinkLoading(prev => ({ ...prev, [subscriptionId]: true }));
        try {
            const res = await api.post(
                `${process.env.REACT_APP_API_BASE}/api/payment/create-order`,
                { subscriptionId },
                { headers: { Authorization: `Bearer ${getToken()}` } }
            );
            const { paymentUrl, amount } = res.data;
            await navigator.clipboard.writeText(paymentUrl);
            message.success(`Payment link (₹${amount}) copied! Share it with the customer.`, 5);
        } catch (err) {
            message.error(err.response?.data?.message || 'Failed to create payment link.');
        } finally {
            setPaymentLinkLoading(prev => ({ ...prev, [subscriptionId]: false }));
        }
    };

    const columns = [
        { title: 'Username', dataIndex: 'username', key: 'username' },
        { title: 'Unique Id', dataIndex: '_id', key: '_id' },
        { title: 'Email', dataIndex: 'email', key: 'email' },
        { title: 'Last login', dataIndex: 'lastLogin', key: 'lastLogin' },
        {
            title: 'Violation Count',
            dataIndex: 'violationValidationCount',
            key: 'violationValidationCount',
            render: (value, record) => (
                <Input
                    type="number"
                    min={0}
                    value={editCounts[record._id] ?? value}
                    onChange={(e) => handleCountChange(record._id, e.target.value)}
                    onBlur={() => saveViolationCount(record._id)}
                    onPressEnter={() => saveViolationCount(record._id)}
                    style={{ width: '80px' }}
                />
            ),
        },
        {
            title: 'Subscriptions',
            dataIndex: 'subscriptions',
            key: 'subscriptions',
            render: (subscriptions) => subscriptions.length,
        },
    ];

    const expandedRowRender = (user) => (
        <Table
            dataSource={user.subscriptions}
            columns={[
                { title: 'Product Name', dataIndex: 'productName', key: 'productName' },
                {
                    title: 'Tier',
                    dataIndex: 'tier',
                    key: 'tier',
                    render: (tier, record) => (
                        <Popconfirm
                            title={`Change ${record.productName} tier to ${tier === 'paid' ? 'Free' : 'Paid'}?`}
                            onConfirm={() => handleToggleSubTier(record._id, tier || 'free')}
                            okText="Yes"
                            cancelText="No"
                        >
                            <Button
                                size="small"
                                type={tier === 'paid' ? 'primary' : 'default'}
                                loading={tierLoading[record._id]}
                                icon={<CreditCardOutlined />}
                            >
                                {tier === 'paid' ? 'Paid' : 'Free'}
                            </Button>
                        </Popconfirm>
                    ),
                },
                {
                    title: 'Status',
                    dataIndex: 'subscriptionStatus',
                    key: 'subscriptionStatus',
                    render: (status) => status.charAt(0).toUpperCase() + status.slice(1),
                },
                { title: 'Subscription ID', dataIndex: 'subscriptionID', key: 'subscriptionID' },
                { title: 'Version', dataIndex: 'version', key: 'version', render: (v) => v || 'N/A' },
                {
                    title: 'Instance',
                    dataIndex: 'activeInstance',
                    key: 'activeInstance',
                    render: (instance, record) => {
                        if (!INSTANCE_PRODUCTS.includes(String(record.productName || '').toUpperCase())) return 'N/A';
                        if (!instance?.instanceId) return <Tag>Unclaimed</Tag>;
                        return (
                            <Space direction="vertical" size={2}>
                                <Tooltip title={`Instance ${instance.instanceId}`}>
                                    <span>{instance.hostname || 'unknown host'} · {instance.ip || 'unknown IP'}</span>
                                </Tooltip>
                                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                                    Last seen {instance.lastSeen ? new Date(instance.lastSeen).toLocaleString() : 'never'}
                                </Typography.Text>
                                <Popconfirm
                                    title={`Release ${record.productName} instance?`}
                                    description="The next instance to check in claims this license. Make sure the customer's old server is stopped first, or it may claim it again."
                                    onConfirm={() => handleReleaseInstance(record._id)}
                                    okText="Release"
                                    okType="danger"
                                    cancelText="Cancel"
                                >
                                    <Button size="small" danger icon={<DisconnectOutlined />} loading={releaseLoading[record._id]}>
                                        Release
                                    </Button>
                                </Popconfirm>
                            </Space>
                        );
                    },
                },
                {
                    title: 'Issue Date',
                    dataIndex: 'issueDate',
                    key: 'issueDate',
                    render: (date) => new Date(date).toLocaleDateString(),
                },
                {
                    title: 'EULA',
                    dataIndex: 'eulaAccepted',
                    key: 'eulaAccepted',
                    render: (accepted, record) => {
                        const nextAccepted = !Boolean(accepted);
                        return (
                            <Space>
                                <Tag color={accepted ? 'green' : 'default'}>
                                    {accepted ? 'Agreed' : 'Unaccepted'}
                                </Tag>
                                <Popconfirm
                                    title={'Mark ' + record.productName + ' EULA as ' + (nextAccepted ? 'Agreed' : 'Unaccepted') + '?'}
                                    description={'This changes the EULA status for ' + user.username + '\'s ' + record.productName + ' subscription.'}
                                    onConfirm={() => handleSetEulaStatus(user._id, record._id, record.productName, accepted)}
                                    okText={nextAccepted ? 'Mark Agreed' : 'Mark Unaccepted'}
                                    okType={nextAccepted ? 'primary' : 'danger'}
                                    cancelText="Cancel"
                                >
                                    <Button
                                        size="small"
                                        type={nextAccepted ? 'primary' : 'default'}
                                        danger={!nextAccepted}
                                        icon={nextAccepted ? <CheckCircleOutlined /> : <StopOutlined />}
                                        loading={eulaLoading[record._id]}
                                    >
                                        {nextAccepted ? 'Mark Agreed' : 'Mark Unaccepted'}
                                    </Button>
                                </Popconfirm>
                            </Space>
                        );
                    },
                },
                {
                    title: 'EULA Accepted At',
                    dataIndex: 'eulaAcceptedAt',
                    key: 'eulaAcceptedAt',
                    render: (date) => date ? new Date(date).toLocaleString() : 'N/A',
                },
                {
                    title: 'Expiration Date',
                    dataIndex: 'expirationDate',
                    key: 'expirationDate',
                    render: (date) => new Date(date).toLocaleDateString(),
                },
                {
                    title: 'Renewal Count',
                    dataIndex: 'renewalCount',
                    key: 'renewalCount',
                    render: (count) => (typeof count === 'undefined' ? 'N/A' : count),
                },
                {
                    title: 'Payment Status',
                    dataIndex: 'paymentStatus',
                    key: 'paymentStatus',
                    render: (status) => {
                        const colorMap = { paid: 'green', pending: 'orange', failed: 'red', not_required: 'default' };
                        return (
                            <Tag color={colorMap[status] || 'default'}>
                                {(status || 'N/A').replace('_', ' ').toUpperCase()}
                            </Tag>
                        );
                    },
                },
                {
                    title: 'Renewal Status',
                    dataIndex: 'renewalStatus',
                    key: 'renewalStatus',
                    render: (status) => {
                        if (!status || Object.keys(status).length === 0) return 'N/A';
                        return Object.entries(status).map(([k, v]) => `${k}: ${v}`).join(', ');
                    },
                },
                {
                    title: 'Payment Link',
                    key: 'paymentLink',
                    render: (_, record) => (
                        <Tooltip title={record.tier === 'paid' ? 'Create Cashfree payment link' : 'Product must be Paid tier'}>
                            <Button
                                size="small"
                                icon={<CopyOutlined />}
                                loading={paymentLinkLoading[record._id]}
                                disabled={record.tier !== 'paid'}
                                onClick={() => handleCreatePaymentLink(record._id, record.tier)}
                            >
                                Get Link
                            </Button>
                        </Tooltip>
                    ),
                },
                {
                    title: 'Backup',
                    key: 'backup',
                    render: (_, record) => {
                        if (record.productName !== 'BAR' || !record.isCloud) return 'N/A';
                        const job = backupJobs[record._id];
                        const active = job && ['queued', 'creating', 'preparing', 'uploading', 'uncertain'].includes(job.stage);
                        const colors = { completed: 'green', failed: 'red', uncertain: 'orange', uploading: 'blue', preparing: 'blue', queued: 'gold' };
                        return <Space direction="vertical" size={4}>
                            {job && <Tooltip title={job.error?.message || (job.s3Key ? 's3://' + job.s3Key : '')}>
                                <Tag color={colors[job.stage] || 'default'}>{job.stage.toUpperCase()}</Tag>
                            </Tooltip>}
                            <Popconfirm title="Back up this BAR account to private S3?"
                                onConfirm={() => handleBackup(record._id)} disabled={active}>
                                <Button size="small" icon={<CloudUploadOutlined />}
                                    loading={backupLoading[record._id]} disabled={active}>
                                    Backup to S3
                                </Button>
                            </Popconfirm>
                            {job?.error?.message && <Typography.Text type="danger">{job.error.message}</Typography.Text>}
                        </Space>;
                    },
                },
            ]}
            pagination={false}
            rowKey="_id"
        />
    );

    return (
        <div style={{ padding: '20px' }}>
            <Title level={2}>Admin Dashboard</Title>
            <Space style={{ marginBottom: 16 }} wrap>
                <Button onClick={() => navigate('/logs')}>Logs</Button>
                <Button onClick={() => navigate('/lead-logs')}>Lead Logs</Button>
                <Button onClick={() => navigate('/ai-keys')}>Manage AI Keys</Button>
                <Button onClick={() => navigate('/activity')}>Admin Activity</Button>
                <Button icon={<DollarOutlined />} onClick={() => setPricingVisible(true)} type="dashed">
                    Product Pricing
                </Button>
                <Button icon={<SafetyOutlined />} onClick={() => setWhitelistVisible(true)} type="dashed">
                    License IP Whitelist
                </Button>
                <Checkbox
                    checked={revokeAllProducts}
                    onChange={(e) => setRevokeAllProducts(e.target.checked)}
                >
                    All products
                </Checkbox>
                <Select
                    mode="multiple"
                    value={revokeProducts}
                    disabled={revokeAllProducts}
                    onChange={setRevokeProducts}
                    style={{ minWidth: 260 }}
                    placeholder="Select products"
                    maxTagCount="responsive"
                >
                    {PRODUCTS.map((p) => <Option key={p} value={p}>{p}</Option>)}
                </Select>
                <Popconfirm
                    title="Revoke EULA acceptance?"
                    description={revokeAllProducts
                        ? 'All users for all products will need to accept the latest EULA on their next login.'
                        : 'Users for the selected products will need to accept the latest EULA on their next login.'}
                    onConfirm={handleRevokeEulaAcceptance}
                    okText="Revoke"
                    okType="danger"
                    cancelText="Cancel"
                >
                    <Button
                        danger
                        icon={<StopOutlined />}
                        loading={bulkEulaLoading}
                        disabled={!revokeAllProducts && revokeProducts.length === 0}
                    >
                        Revoke EULA
                    </Button>
                </Popconfirm>
            </Space>
            <br />
            <Space style={{ marginBottom: '20px' }}>
                <Search placeholder="Search by username or email" onSearch={onSearch} enterButton style={{ width: 300 }} />
            </Space>
            <Table
                columns={columns}
                dataSource={filteredData}
                expandable={{ expandedRowRender }}
                rowKey="_id"
                pagination={{ pageSize: 10 }}
            />

            {/* ── Suspended EAA Subscriptions ─────────────────────────────── */}
            <Divider />
            <Space style={{ marginBottom: 12 }}>
                <StopOutlined style={{ color: '#ff4d4f', fontSize: 18 }} />
                <Title level={4} style={{ margin: 0 }}>
                    Suspended EAA Subscriptions
                    {suspendedRows.length > 0 && (
                        <Tag color="red" style={{ marginLeft: 8 }}>{suspendedRows.length}</Tag>
                    )}
                </Title>
            </Space>
            <Table
                dataSource={suspendedRows}
                rowKey="subscriptionId"
                pagination={{ pageSize: 10 }}
                locale={{ emptyText: 'No suspended EAA subscriptions.' }}
                columns={[
                    { title: 'Username', dataIndex: 'username', key: 'username' },
                    { title: 'Email', dataIndex: 'email', key: 'email' },
                    { title: 'Sender Identity', dataIndex: 'emailIdentity', key: 'emailIdentity' },
                    {
                        title: 'License Key',
                        dataIndex: 'licenseKey',
                        key: 'licenseKey',
                        render: (k) => <Typography.Text code copyable>{k}</Typography.Text>,
                    },
                    {
                        title: 'Reason',
                        dataIndex: 'blockReason',
                        key: 'blockReason',
                        render: (reason) => (
                            <Tooltip title={reason}>
                                <Tag color="red" style={{ maxWidth: 260, whiteSpace: 'normal' }}>
                                    {reason}
                                </Tag>
                            </Tooltip>
                        ),
                    },
                    {
                        title: 'Action',
                        key: 'action',
                        render: (_, record) => (
                            <Popconfirm
                                title={`Unsuspend subscription for ${record.username}?`}
                                description="This will immediately re-enable email sending for this account."
                                onConfirm={() => handleUnsuspend(record.licenseKey, record.subscriptionId)}
                                okText="Unsuspend"
                                okType="danger"
                                cancelText="Cancel"
                            >
                                <Button
                                    size="small"
                                    type="primary"
                                    danger
                                    icon={<CheckCircleOutlined />}
                                    loading={unsuspendLoading[record.subscriptionId]}
                                >
                                    Unsuspend
                                </Button>
                            </Popconfirm>
                        ),
                    },
                ]}
            />

            <PricingModal visible={pricingVisible} onClose={() => setPricingVisible(false)} />
            <WhitelistModal visible={whitelistVisible} onClose={() => setWhitelistVisible(false)} getToken={getToken} />
        </div>
    );
};

export default Dashboard;
